import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { CourseService } from '@/services';
import type { Exercise, Question } from '@/types';
import { getMarking } from '@/utils/server/course';

export function useUpdateUserExercise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      courseId,
      exercise,
      submit = false,
    }: {
      courseId: string;
      exercise: Exercise;
      submit: boolean;
    }) =>
      CourseService.patchUserExercise(courseId, {
        ...exercise,
        status: submit ? 'submitted' : 'active',
      }),
    onSuccess: (updatedExercise) => {
      queryClient.setQueryData<Exercise>(
        ['chapters', updatedExercise.type.toLowerCase(), updatedExercise.id],
        updatedExercise,
      );
      queryClient.invalidateQueries({
        queryKey: ['userCourseStatus'],
      });
    },
    onError: () => {
      enqueueSnackbar('Übung konnte nicht gespeichert werden.', { variant: 'error' });
    },
  });
}

export function useRestartUserExercise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ courseId, exerciseId }: { courseId: string; exerciseId: string }) =>
      CourseService.getNewUserExercise(courseId ?? '', exerciseId),
    onSuccess: (updatedExercise) => {
      queryClient.setQueryData<Exercise>(
        ['chapters', updatedExercise.type.toLowerCase(), updatedExercise.id],
        updatedExercise,
      );
      queryClient.invalidateQueries({ queryKey: ['userCourseStatus'] });
    },
  });
}

export function useQuestionMarking(courseId: string) {
  const queryClient = useQueryClient();

  const { mutate: updateMarking } = useMutation({
    mutationFn: async ({
      question,
      update,
      exercise,
    }: {
      question: Question;
      update: string | string[];
      exercise: Exercise;
    }) => {
      const marking = getMarking(question.type, question.marking, update);
      const updatedExercise = {
        ...exercise,
        questions: exercise.questions?.map((q) => (q.id === question.id ? { ...q, marking } : q)),
      };

      return CourseService.patchUserExercise(courseId, {
        ...updatedExercise,
        status: 'active',
      });
    },
    onMutate: async ({ question, update, exercise }) => {
      const queryKey = ['chapters', exercise.type.toLowerCase(), exercise.id] as const;
      const previousExercise = queryClient.getQueryData<Exercise>(queryKey);
      const marking = getMarking(question.type, question.marking, update);
      const updatedExercise = {
        ...exercise,
        questions: exercise.questions?.map((q) => (q.id === question.id ? { ...q, marking } : q)),
      };

      queryClient.setQueryData(queryKey, updatedExercise);

      return { previousExercise, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousExercise && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousExercise);
      }
      enqueueSnackbar('Antwort konnte nicht gespeichert werden.', { variant: 'error' });
    },
    onSuccess: (updatedExercise, { exercise }) => {
      const queryKey = [
        'chapters',
        updatedExercise.type.toLowerCase(),
        updatedExercise.id,
      ] as const;
      const cachedExercise = queryClient.getQueryData<Exercise>(queryKey) ?? exercise;

      const mergedQuestions = cachedExercise.questions?.map((cachedQuestion) => {
        const serverQuestion = updatedExercise.questions?.find(
          (question) => question.id === cachedQuestion.id,
        );
        if (!serverQuestion) return cachedQuestion;

        return {
          ...cachedQuestion,
          marking: serverQuestion.marking,
          correct: serverQuestion.correct,
        };
      });

      queryClient.setQueryData<Exercise>(queryKey, {
        ...cachedExercise,
        statistics: updatedExercise.statistics,
        status: updatedExercise.status,
        questions: mergedQuestions,
      });
    },
  });

  return { updateMarking };
}
